import React from 'react';
import IndicatorCard from '../shared/IndicatorCard';
import { INDICATOR_GROUPS, getPeriod } from '../config';

/**
 * Every tracked indicator, grouped by category, each showing its current value
 * beside the same value one comparison period ago.
 */
const OverviewTab = ({ data, period }) => {
  const { marketData, loading, hasError, error, refresh, getComparison } = data;
  const selectedPeriod = getPeriod(period);

  return (
    <>
      <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">📡</span>
            <div>
              <h2 className="font-semibold text-blue-900">Real-Time vs Historical Market Data</h2>
              <p className="text-sm text-blue-700">
                Current market conditions alongside historical context ({selectedPeriod.label} comparison).
                Data feeds directly into RateLens simulations from Federal Reserve, Treasury, and market sources.
              </p>
            </div>
          </div>

          <div className="text-right space-y-2">
            <div
              className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
                hasError ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${hasError ? 'bg-red-500' : 'bg-green-500'}`} />
              <span>{hasError ? 'Connection Error' : 'Live Data'}</span>
            </div>
            <div className="text-xs text-blue-600 flex items-center justify-end space-x-2">
              <span>{selectedPeriod.icon}</span>
              <span>{selectedPeriod.description}</span>
            </div>
          </div>
        </div>
      </div>

      {historicalSetupNeeded(error) && <FredSetupNotice />}

      {hasError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
          <div className="flex items-start space-x-3">
            <span className="text-red-600 text-xl">⚠️</span>
            <div>
              <h3 className="font-semibold text-red-800">
                {!error.marketData ? 'Historical comparison unavailable' : 'Unable to load market data'}
              </h3>
              <div className="text-red-700 text-sm mt-1 space-y-2">
                {error.marketData && (
                  <div>
                    <span className="font-semibold">Current data:</span> {error.marketData}
                  </div>
                )}
                {error.historicalData && (
                  <div>
                    <span className="font-semibold">Historical data:</span> {error.historicalData}
                  </div>
                )}
              </div>
              <button onClick={refresh} className="text-sm text-red-600 hover:text-red-800 mt-2 underline">
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && !marketData && (
        <div className="text-center py-16">
          <div className="inline-flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            <span className="text-lg text-gray-600">Loading market data...</span>
          </div>
        </div>
      )}

      {marketData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {INDICATOR_GROUPS.map((group) => (
            <div key={group.category} className={`bg-white rounded-lg shadow-md border-l-4 ${group.color} p-6`}>
              <div className="flex items-center space-x-3 mb-6">
                <span className="text-2xl">{group.icon}</span>
                <h3 className="text-lg font-semibold text-gray-900">{group.category}</h3>
              </div>

              <div className="space-y-6">
                {group.items.map((item) => (
                  <IndicatorCard
                    key={item.key}
                    item={item}
                    marketData={marketData}
                    period={period}
                    getComparison={getComparison}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const historicalSetupNeeded = (error) => Boolean(error.historicalData?.includes('FRED API key'));

const FredSetupNotice = () => (
  <div className="mb-8 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
    <div className="flex items-start space-x-3">
      <span className="text-yellow-600 text-xl mt-1">ℹ️</span>
      <div>
        <h3 className="font-medium text-yellow-900">Historical Data Setup Needed</h3>
        <p className="text-sm text-yellow-700 mb-2">
          Historical comparisons require a FRED API key to be configured on the server.
        </p>
        <div className="bg-white p-3 rounded text-xs text-gray-700">
          <p className="font-semibold mb-1">Administrator setup instructions:</p>
          <ol className="list-decimal list-inside space-y-1">
            <li>
              Obtain a free FRED API key from{' '}
              <a
                href="https://fred.stlouisfed.org/docs/api/api_key.html"
                className="text-blue-600 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                St. Louis Fed
              </a>
            </li>
            <li>
              In the backend folder, run: <code className="bg-gray-100 px-1">run_with_fred_api.ps1 YOUR_API_KEY</code>
            </li>
            <li>
              For persistent configuration, set the <code className="bg-gray-100 px-1">RATELENS_FRED_API_KEY</code>{' '}
              environment variable
            </li>
          </ol>
        </div>
      </div>
    </div>
  </div>
);

export default OverviewTab;
